import * as THREE from 'three';
import { COLORS, SIMULATION } from '../game/constants';

export class PlayerShotSystem {
  readonly mesh: THREE.InstancedMesh;
  private readonly active = new Uint8Array(SIMULATION.maxPlayerShots);
  private readonly x = new Float32Array(SIMULATION.maxPlayerShots);
  private readonly y = new Float32Array(SIMULATION.maxPlayerShots);
  private readonly vx = new Float32Array(SIMULATION.maxPlayerShots);
  private readonly vy = new Float32Array(SIMULATION.maxPlayerShots);
  private readonly geometry = new THREE.CapsuleGeometry(0.055, 0.46, 2, 6);
  private readonly material = new THREE.MeshBasicMaterial({
    color: COLORS.player,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly dummy = new THREE.Object3D();
  count = 0;

  constructor() {
    this.mesh = new THREE.InstancedMesh(
      this.geometry,
      this.material,
      SIMULATION.maxPlayerShots,
    );
    this.mesh.name = 'HailstormRounds';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 22;
  }

  /**
   * Fire the current weapon at its current power tier.
   *
   * HAILSTORM widens as it powers up — 2 pellets at tier 0 to 9 at tier 7,
   * spreading from 0 to 34 degrees. ARCLIGHT stays narrow but multiplies into
   * parallel lances. Both are quoted from bible 06 Appendix A.
   */
  fire(x: number, y: number, weapon: 'HAILSTORM' | 'ARCLIGHT', power: number): void {
    const tier = Math.max(0, Math.min(7, power));
    if (weapon === 'HAILSTORM') {
      const count = 2 + tier;
      const spreadDeg = [0, 6, 10, 14, 19, 24, 29, 34][tier];
      for (let i = 0; i < count; i += 1) {
        const t = count === 1 ? 0 : i / (count - 1) - 0.5;
        const a = Math.PI / 2 + t * spreadDeg * (Math.PI / 180);
        this.spawnAt(x, y, Math.cos(a), Math.sin(a));
      }
      return;
    }
    // ARCLIGHT: 1 lance at low tiers, up to 4 parallel at tier 7.
    const lances = [1, 1, 2, 2, 3, 3, 4, 4][tier];
    const spread = [0, 0, 1.1, 1.1, 1.4, 1.4, 2.0, 2.0][tier];
    for (let i = 0; i < lances; i += 1) {
      const t = lances === 1 ? 0 : i / (lances - 1) - 0.5;
      this.spawnAt(x + t * spread, y, 0, 1);
    }
  }

  private spawnAt(x: number, y: number, dx: number, dy: number): void {
    this.spawn(x, y, dx * 62, dy * 62);
  }

  spawnPair(x: number, y: number): void {
    this.spawn(x - 0.24, y + 1.1, -0.35, 38);
    this.spawn(x + 0.24, y + 1.1, 0.35, 38);
  }

  update(delta: number): void {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      this.x[index] += this.vx[index] * delta;
      this.y[index] += this.vy[index] * delta;
      if (this.y[index] > 28 || Math.abs(this.x[index]) > 22) this.deactivate(index);
    }
  }

  /**
   * Consume EVERY shot inside the radius and return how many landed.
   *
   * `collideCircle` consumes at most one shot per call, so a per-frame caller
   * applying fixed damage produces a constant DPS independent of the player's
   * actual rate of fire — which is how the stage boss ended up dying in six
   * seconds. Damage must be proportional to rounds that connect.
   */
  /**
   * Visit every live round. Lets callers resolve shots against systems that
   * own their own collision (props, terrain) without this class needing to
   * know about them.
   */
  forEachActive(fn: (x: number, y: number, index: number) => void): void {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      fn(this.x[index], this.y[index], index);
    }
  }

  /** Remove a specific round — used when it is consumed by a prop hit. */
  consume(index: number): void {
    if (!this.active[index]) return;
    this.deactivate(index);
  }

  collideAll(x: number, y: number, radius: number): number {
    let hits = 0;
    const r2 = radius * radius;
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const dx = this.x[index] - x;
      const dy = this.y[index] - y;
      if (dx * dx + dy * dy > r2) continue;
      this.deactivate(index);
      hits += 1;
    }
    return hits;
  }

  collideCircle(x: number, y: number, radius: number): boolean {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const dx = this.x[index] - x;
      const dy = this.y[index] - y;
      if (dx * dx + dy * dy <= radius * radius) {
        this.deactivate(index);
        return true;
      }
    }
    return false;
  }

  syncVisuals(): void {
    let visualIndex = 0;
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      this.dummy.position.set(this.x[index], this.y[index], 1.1);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.set(1, 1, 1);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(visualIndex, this.dummy.matrix);
      visualIndex += 1;
    }
    this.mesh.count = visualIndex;
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  reset(): void {
    this.active.fill(0);
    this.count = 0;
    this.syncVisuals();
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  private spawn(x: number, y: number, vx: number, vy: number): void {
    for (let index = 0; index < this.active.length; index += 1) {
      if (this.active[index]) continue;
      this.active[index] = 1;
      this.x[index] = x;
      this.y[index] = y;
      this.vx[index] = vx;
      this.vy[index] = vy;
      this.count += 1;
      return;
    }
  }

  private deactivate(index: number): void {
    this.active[index] = 0;
    this.count -= 1;
  }
}
